import React from "react";
import { Padding } from "../../BuildingBlocks/Padding";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMEmptyPlaceholder } from "../../Simple/CRMEmptyPlaceholder/CRMEmptyPlaceholder";
import { CRMButtonIcon } from "../../CRMButtonIcon/CRMButtonIcon";
import { CRMButtonPrimary } from "../../CRMButtonPrimary/CRMButtonPrimary";
import { CRMButtonSecondary } from "../../CRMButtonSecondary/CRMButtonSecondary";
import { CRMCardOutside } from "../../CRMCardOutside/CRMCardOutside";
import { Text } from "../../BuildingBlocks/Text";
import { Background } from "../../BuildingBlocks/Background";
import { CRMColors } from "../../../models/CRMColors";
import { CRMFontSizes } from "../../../models/CRMFontSizes";

type TBucket = "good_news" | "steady" | "action_needed";

type TRow = {
    case_id: string;
    address_short_string: string;
    client_first_names: Array<string>;
    introducer_name: string | null;
    bucket: TBucket;
    days_since_last_update: number | null;
    has_sail_homes_listing: boolean;
};

type TDraftResponse = {
    case_id: string;
    bucket: TBucket;
    subject: string;
    body: string;
    send_to: Array<string>;
    cc_addresses: Array<string>;
    address: string;
};

type TContextItem = { label: string; date: string | null; detail: string | null };

type TCaseContext = {
    case_status: string;
    transaction_type: string;
    address: string;
    client_first_names: Array<string>;
    has_sail_homes_listing: boolean;
    last_sent_at: string | null;
    completed_recently: Array<TContextItem>;
    outstanding_client: Array<string>;
    outstanding_team: Array<string>;
    ad_hoc_jobs: Array<{ job: string; days_outstanding: number }>;
    pending_chase_reasons: Array<string>;
    chasing: Array<string>;
    recent_emails: Array<TContextItem>;
    recent_inbound_emails: Array<TContextItem>;
    sh_enquiries_in_window: number;
    sh_viewings_in_window: number;
    sh_offers_in_window: number;
    sh_offer_accepted_in_window: boolean;
    sh_completed_in_window: boolean;
};

type TDraftWithContext = {
    draft: TDraftResponse;
    context: TCaseContext;
};

type TCRMWeeklyUpdatesBlockProps = {
    openCaseId: string | null;
    onPreloadDraft: (payload: TDraftResponse) => void;
    onClosePanel: () => void;
};

const bucketLabel = (b: TBucket): string =>
    b === "good_news" ? "Good news"
    : b === "action_needed" ? "Action needed"
    : "Steady";

const bucketAccent = (b: TBucket) =>
    b === "good_news" ? CRMColors.HIGHLIGHTS_POSITIVE_GREEN_2
    : b === "action_needed" ? CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_2
    : CRMColors.NEUTRAL_8;

const bucketPillBg = (b: TBucket) =>
    b === "good_news" ? CRMColors.HIGHLIGHTS_POSITIVE_GREEN_2
    : b === "action_needed" ? CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_2
    : CRMColors.NEUTRAL_4;

const ageLabel = (days: number | null): string => {
    if (days === null) return "Never updated";
    if (days === 0) return "Sent today";
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
};

export const CRMWeeklyUpdatesBlock = (props: TCRMWeeklyUpdatesBlockProps): JSX.Element => {
    const [rows, setRows] = React.useState<Array<TRow> | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [openingCaseId, setOpeningCaseId] = React.useState<string | null>(null);
    const [skipModal, setSkipModal] = React.useState<{ case_id: string; address: string } | null>(null);
    const [panelContext, setPanelContext] = React.useState<TCaseContext | null>(null);

    const fetchList = React.useCallback(async () => {
        setError(null);
        try {
            const response = await fetch(`${env.REACT_APP_API_URL}/v1/weekly-updates/cases`, {
                credentials: "include",
                method: "GET",
                headers: { "accept": "application/json" },
            });
            if (!response.ok) {
                setError("Could not load weekly updates");
                return;
            }
            const body = await response.json() as { data: { rows: Array<TRow> } };
            setRows(body.data.rows);
        } catch {
            setError("Could not load weekly updates");
        }
    }, []);

    React.useEffect(() => {
        fetchList();
    }, [fetchList]);

    // Close the panel only on a genuine open→closed transition of the global
    // open-case id (send success, skip, or back) — refreshing the list so the
    // just-handled case reflects its new state.
    //
    // We MUST guard on the previous value. panelContext (local state) is set
    // synchronously the moment the draft fetch returns, but openCaseId (global
    // state) only updates after the dispatched action runs through the rxjs
    // pipeline. Without the prev-guard, the brief window where panelContext is
    // set but openCaseId is still null would satisfy `openCaseId === null` and
    // wipe the panel before it ever shows.
    const prevOpenCaseId = React.useRef(props.openCaseId);
    React.useEffect(() => {
        const was = prevOpenCaseId.current;
        prevOpenCaseId.current = props.openCaseId;
        if (was !== null && props.openCaseId === null && panelContext !== null) {
            setPanelContext(null);
            fetchList();
        }
    }, [props.openCaseId, panelContext, fetchList]);

    const openCase = async (caseId: string): Promise<void> => {
        setOpeningCaseId(caseId);
        try {
            const response = await fetch(`${env.REACT_APP_API_URL}/v1/weekly-updates/draft`, {
                credentials: "include",
                method: "POST",
                headers: { "accept": "application/json", "content-type": "application/json" },
                body: JSON.stringify({ case_id: caseId }),
            });
            if (!response.ok) {
                setError("Could not draft the update");
                setOpeningCaseId(null);
                return;
            }
            const body = await response.json() as { data: TDraftWithContext };
            setPanelContext(body.data.context);
            props.onPreloadDraft(body.data.draft);
        } catch {
            setError("Could not draft the update");
        }
        setOpeningCaseId(null);
    };

    const skipCase = async (caseId: string, snoozeWeeks: 1 | 2 | 4, reason: string): Promise<void> => {
        try {
            const response = await fetch(`${env.REACT_APP_API_URL}/v1/weekly-updates/skip`, {
                credentials: "include",
                method: "POST",
                headers: { "accept": "application/json", "content-type": "application/json" },
                body: JSON.stringify({ case_id: caseId, snooze_weeks: snoozeWeeks, skip_reason: reason }),
            });
            if (!response.ok) {
                setError("Could not skip");
                return;
            }
            setSkipModal(null);
            await fetchList();
        } catch {
            setError("Could not skip");
        }
    };

    if (panelContext !== null) {
        return <CaseStatePanel context={panelContext} onBack={props.onClosePanel} />;
    }

    if (rows === null && error === null) {
        return (
            <Padding spacing={CRMSpacing.X_LARGE}>
                <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_4}>Loading…</Text>
            </Padding>
        );
    }

    if (rows !== null && rows.length === 0) {
        return (
            <Padding spacing={CRMSpacing.X_LARGE}>
                <CRMEmptyPlaceholder iconName="send-email" iconSize="140px">
                    No cases due an update this week.
                </CRMEmptyPlaceholder>
            </Padding>
        );
    }

    return (
        <div className="crm-weekly-updates-block">
            <Padding spacing={CRMSpacing.MEDIUM}>
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    {error !== null && (
                        <Background
                            color={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_12}
                            borderRadius="4px"
                            padding={`${CRMSpacing.SMALL} ${CRMSpacing.MEDIUM}`}
                        >
                            <Text size={CRMFontSizes.SMALL} color={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_2}>{error}</Text>
                        </Background>
                    )}
                    {(rows ?? []).map((r) => (
                        <Background
                            key={r.case_id}
                            color={CRMColors.NEUTRAL_PAPER}
                            borderDefinition={`1px solid ${CRMColors.NEUTRAL_10}`}
                            borderLeftDefinition={`4px solid ${bucketAccent(r.bucket)}`}
                            borderRadius="6px"
                            padding={CRMSpacing.MEDIUM}
                        >
                            <SpacingRow
                                spacing={CRMSpacing.MEDIUM}
                                justifyContent="space-between"
                                alignItems="flex-start"
                            >
                                <SpacingColumn spacing={CRMSpacing.TINY}>
                                    <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                                        <Background
                                            color={bucketPillBg(r.bucket)}
                                            borderRadius="100px"
                                            padding="4px 10px"
                                        >
                                            <Text size={CRMFontSizes.X_SMALL} weight={600} lineHeight={1} color={CRMColors.NEUTRAL_PAPER}>
                                                {bucketLabel(r.bucket)}
                                            </Text>
                                        </Background>
                                        <a
                                            className="crm-weekly-updates-row__address-link"
                                            href={`/crm/legal/cases/${r.case_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <Text size={CRMFontSizes.SMALL} weight={600} color={CRMColors.NEUTRAL_0}>
                                                {r.address_short_string || "(no address)"}
                                            </Text>
                                        </a>
                                        {r.has_sail_homes_listing && (
                                            <Background color={CRMColors.PRIMARY_13} borderRadius="4px" padding="2px 6px">
                                                <Text size={CRMFontSizes.X_SMALL} weight={700} color={CRMColors.PRIMARY_2}>SH</Text>
                                            </Background>
                                        )}
                                    </SpacingRow>
                                    <SpacingRow spacing={CRMSpacing.MEDIUM}>
                                        <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_2}>
                                            {r.client_first_names.length > 0 ? r.client_first_names.join(", ") : "Client"}
                                        </Text>
                                        {r.introducer_name !== null && (
                                            <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_2}>CC: {r.introducer_name}</Text>
                                        )}
                                        <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_2}>
                                            {ageLabel(r.days_since_last_update)}
                                        </Text>
                                    </SpacingRow>
                                </SpacingColumn>
                                <SpacingRow spacing={CRMSpacing.TINY} alignItems="center">
                                    <CRMButtonIcon
                                        variant="quaternary"
                                        icon="snooze"
                                        title="Skip this week"
                                        onClick={() => setSkipModal({ case_id: r.case_id, address: r.address_short_string })}
                                    />
                                    <CRMButtonIcon
                                        variant="quaternary"
                                        icon="pencil"
                                        title={openingCaseId === r.case_id ? "Drafting…" : "Draft update"}
                                        disabled={openingCaseId !== null}
                                        onClick={() => openCase(r.case_id)}
                                    />
                                </SpacingRow>
                            </SpacingRow>
                        </Background>
                    ))}
                </SpacingColumn>
            </Padding>

            {skipModal !== null && (
                <SkipModal
                    address={skipModal.address}
                    onCancel={() => setSkipModal(null)}
                    onConfirm={(snoozeWeeks, reason) => skipCase(skipModal.case_id, snoozeWeeks, reason)}
                />
            )}
        </div>
    );
};

type TSkipModalProps = {
    address: string;
    onCancel: () => void;
    onConfirm: (snoozeWeeks: 1 | 2 | 4, reason: string) => void;
};

const SkipModal = (props: TSkipModalProps): JSX.Element => {
    const [snoozeWeeks, setSnoozeWeeks] = React.useState<1 | 2 | 4>(1);
    const [reason, setReason] = React.useState<string>("");
    return (
        <div className="crm-weekly-updates-skip-modal__backdrop">
            <div className="crm-weekly-updates-skip-modal__panel">
                <Padding spacing={CRMSpacing.LARGE}>
                    <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                        <h3 className="crm-weekly-updates-skip-modal__title">
                            Skip weekly update — {props.address || "(no address)"}
                        </h3>
                        <div className="crm-weekly-updates-skip-modal__field">
                            <label htmlFor="snooze-weeks">Snooze for:</label>
                            <select
                                id="snooze-weeks"
                                value={snoozeWeeks}
                                onChange={(e) => setSnoozeWeeks(Number(e.target.value) as 1 | 2 | 4)}
                            >
                                <option value={1}>1 week</option>
                                <option value={2}>2 weeks</option>
                                <option value={4}>4 weeks</option>
                            </select>
                        </div>
                        <div className="crm-weekly-updates-skip-modal__field">
                            <label htmlFor="skip-reason">Reason (optional, for audit):</label>
                            <textarea
                                id="skip-reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                rows={3}
                            />
                        </div>
                        <SpacingRow spacing={CRMSpacing.SMALL} justifyContent="end">
                            <CRMButtonSecondary label="Cancel" onClick={props.onCancel} />
                            <CRMButtonPrimary
                                label="Skip"
                                highlight="instructional-brick"
                                onClick={() => props.onConfirm(snoozeWeeks, reason)}
                            />
                        </SpacingRow>
                    </SpacingColumn>
                </Padding>
            </div>
        </div>
    );
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const friendlyDate = (iso: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 7) return `${days} days ago`;
    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const transactionLabel = (t: string): string =>
    t === "sale" ? "Sale"
    : t === "purchase" ? "Purchase"
    : t === "transfer" ? "Transfer"
    : t === "remortgage" ? "Remortgage"
    : "Case";

type TCaseStatePanelProps = {
    context: TCaseContext;
    onBack: () => void;
};

const CaseStatePanel = (props: TCaseStatePanelProps): JSX.Element => {
    const c = props.context;
    return (
        <div className="crm-weekly-updates-block">
            <Padding spacing={CRMSpacing.MEDIUM}>
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                        <CRMButtonIcon variant="quaternary" icon="arrow-left" title="Back to list" onClick={props.onBack} />
                        <Text size={CRMFontSizes.SMALL_PLUS} weight={600} color={CRMColors.NEUTRAL_0}>
                            {c.address || "(no address)"}
                        </Text>
                    </SpacingRow>

                    <CRMCardOutside borderRounding="none" shadow={true}>
                        <Padding spacing={CRMSpacing.MEDIUM}>
                            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                                <Background
                                    color={CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_12}
                                    borderRadius="6px"
                                    padding={`${CRMSpacing.SMALL} ${CRMSpacing.MEDIUM}`}
                                >
                                    <Text size={CRMFontSizes.X_SMALL} lineHeight={1.4} color={CRMColors.NEUTRAL_2}>
                                        These are the only case facts the draft was written from. If the draft claims something you can't see here, don't trust it — check the case before sending.
                                    </Text>
                                </Background>

                                <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>
                                    {transactionLabel(c.transaction_type)} · {humanise(c.case_status)}
                                    {c.client_first_names.length > 0 ? ` · ${c.client_first_names.join(", ")}` : ""}
                                    {c.last_sent_at ? ` · last update ${friendlyDate(c.last_sent_at)}` : " · never updated"}
                                </Text>

                                <PanelSection variant="client" title="Needs the client" emptyText="Nothing outstanding from the client right now.">
                                    {c.outstanding_client.map((label, i) => (
                                        <PanelItem key={i}>{label}</PanelItem>
                                    ))}
                                </PanelSection>

                                <PanelSection title="Our team" emptyText="No outstanding work on our side.">
                                    {c.outstanding_team.map((label, i) => (
                                        <PanelItem key={i}>{label}</PanelItem>
                                    ))}
                                </PanelSection>

                                <PanelSection title="Our outstanding tasks" emptyText="No ad-hoc tasks open on our side.">
                                    {c.ad_hoc_jobs.map((job, i) => (
                                        <PanelItem key={i} muted={`${job.days_outstanding}d`}>{job.job}</PanelItem>
                                    ))}
                                </PanelSection>

                                <PanelSection title="Completed recently" emptyText="Nothing completed in this window.">
                                    {c.completed_recently.map((item, i) => (
                                        <PanelItem key={i} muted={item.date ? friendlyDate(item.date) : undefined}>{humanise(item.label)}</PanelItem>
                                    ))}
                                </PanelSection>

                                <PanelSection title="Chasing" emptyText="No active chases.">
                                    {c.chasing.map((entity, i) => (
                                        <PanelItem key={i}>{humanise(entity)}</PanelItem>
                                    ))}
                                </PanelSection>

                                <PanelSection title="Chase proposed" emptyText="No chase proposed on this case.">
                                    {c.pending_chase_reasons.map((reason, i) => (
                                        <PanelItem key={i}>{reason}</PanelItem>
                                    ))}
                                </PanelSection>

                                {c.has_sail_homes_listing && (
                                    <PanelSection title="Sail Homes activity" emptyText="No agency activity in this window.">
                                        {c.sh_completed_in_window ? <PanelItem>Sale completed</PanelItem> : null}
                                        {c.sh_offer_accepted_in_window ? <PanelItem>Offer accepted</PanelItem> : null}
                                        {c.sh_viewings_in_window > 0 ? <PanelItem>{c.sh_viewings_in_window} viewing(s) booked</PanelItem> : null}
                                        {c.sh_offers_in_window > 0 && !c.sh_offer_accepted_in_window ? <PanelItem>{c.sh_offers_in_window} offer(s) received</PanelItem> : null}
                                        {c.sh_enquiries_in_window > 0 ? <PanelItem>{c.sh_enquiries_in_window} new buyer enquiry/enquiries</PanelItem> : null}
                                    </PanelSection>
                                )}

                                <PanelSection title="Received since last update" emptyText="Nothing received since the last update.">
                                    {c.recent_inbound_emails.map((item, i) => (
                                        <PanelEmailItem key={i} item={item} />
                                    ))}
                                </PanelSection>

                                <PanelSection title="Recent emails to client" emptyText="No outbound emails on file.">
                                    {c.recent_emails.map((item, i) => (
                                        <PanelEmailItem key={i} item={item} />
                                    ))}
                                </PanelSection>
                            </SpacingColumn>
                        </Padding>
                    </CRMCardOutside>
                </SpacingColumn>
            </Padding>
        </div>
    );
};

const humanise = (token: string): string =>
    token.replace(/_/g, " ").replace(/^./, (ch) => ch.toUpperCase());

type TPanelSectionProps = {
    title: string;
    emptyText: string;
    variant?: "client";
    children: React.ReactNode;
};

const PanelSection = (props: TPanelSectionProps): JSX.Element => {
    const items = React.Children.toArray(props.children).filter(Boolean);
    return (
        <Background borderTopDefinition={`1px solid ${CRMColors.NEUTRAL_10}`} padding={`${CRMSpacing.SMALL} 0px 0px 0px`}>
            <SpacingColumn spacing={CRMSpacing.TINY}>
                <Text
                    size={CRMFontSizes.SMALL}
                    weight={600}
                    color={props.variant === "client" ? CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0 : CRMColors.NEUTRAL_0}
                >
                    {props.title}
                </Text>
                {items.length > 0
                    ? <SpacingColumn spacing={CRMSpacing.TINY}>{items}</SpacingColumn>
                    : <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_6}>{props.emptyText}</Text>}
            </SpacingColumn>
        </Background>
    );
};

type TPanelItemProps = {
    muted?: string;
    children: React.ReactNode;
};

const PanelItem = (props: TPanelItemProps): JSX.Element => (
    <SpacingRow spacing={CRMSpacing.TINY} alignItems="baseline">
        <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2}>{props.children}</Text>
        {props.muted ? <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>— {props.muted}</Text> : null}
    </SpacingRow>
);

type TPanelEmailItemProps = {
    item: TContextItem;
};

const PanelEmailItem = (props: TPanelEmailItemProps): JSX.Element => (
    <SpacingColumn spacing={CRMSpacing.TINY}>
        <PanelItem muted={props.item.date ? friendlyDate(props.item.date) : undefined}>{props.item.label}</PanelItem>
        {props.item.detail && props.item.detail.trim() !== "" ? (
            <Text size={CRMFontSizes.X_SMALL} lineHeight={1.35} color={CRMColors.NEUTRAL_4}>
                {props.item.detail.slice(0, 140)}
            </Text>
        ) : null}
    </SpacingColumn>
);
